/**
 * SedeAziendale – bundle autonomo per il flusso profilo vendor
 * 
 * INPUT (props del componente esportato `SedeAziendale`)
 * - enableValidation (boolean, default false)
 *   Se true, mostra i pulsanti "Verifica" / "Reset" e chiama LocationIQ.
 *   Se false, solo campi testo (nessuna chiamata esterna).
 *
 * - value (oggetto shape come EMPTY_ADDRESS_FORM)
 *   Campi controllati: via, civico, citta, cap, provincia, regione (opzionale), stato.
 *   Se `regione` è vuota non viene inviata a LocationIQ; se valorizzata partecipa alla verifica.
 *
 * - onSedeFieldChange (function(sedeKey, val))
 *   sedeKey è uno di: via | civico | citta | cap | provincia | regione | stato
 *   Il parent aggiorna il proprio stato (es. mappa verso indirizzoVia, indirizzoCitta, …).
 *
 * - hasStoredAddress (boolean)
 *   true se il profilo server aveva già almeno un campo indirizzo valorizzato.
 *   Con validazione attiva, se true e l’utente non ha modificato nulla → niente Verifica/Reset.
 *
 * - addressNeedsReverify (boolean)
 *   true se i valori correnti differiscono dall’ultimo snapshot "verificato" nel parent.
 *
 * - onVerifiedMerge (function(normalizedSedeForm))
 *   Chiamato dopo Verifica OK. Argomento: stesso shape di `value` con valori normalizzati
 *   da LocationIQ. Il parent deve fondare nello stato e aggiornare lo snapshot verificato.
 *
 * - onResetToVerified (function)
 *   Ripristina i campi allo snapshot verificato (es. merge chiavi indirizzo dal parent).
 *
 * - onGeocodeServiceUnavailable (function, opzionale)
 *   Invocato se manca la API key o LocationIQ non risponde (errore rete / HTTP non ok).
 *   Aprire nel parent un dialog con LOCATION_IQ_USER_MESSAGE_IT.
 *
 * - apiKey (string, opzionale)
 *   Default: process.env.REACT_APP_LOCATIONIQ_API_KEY ?? ""
 *
 * - language (opzionale, default "it")
 *   Parametro LocationIQ search (accept-language).
 * 
 * OUTPUT / effetti
 * - fetch verso https://api.locationiq.com/v1/search quando l’utente clicca Verifica.
 * - onSedeFieldChange su ogni keystroke (controlled input).
 * - onVerifiedMerge al successo; onGeocodeServiceUnavailable su outage / key mancante.
 *
 * Export utili per form / Zod nel progetto profilo vendor
 * - EMPTY_ADDRESS_FORM, ADDRESS_FORM_FIELD_KEYS, ADDRESS_SERVICE_CODES
 * - LOCATION_IQ_USER_MESSAGE_IT, CIVICO_UNVERIFIED_TOOLTIP_IT, REGIONE_OPTIONAL_TOOLTIP_IT
 * - validateAddressWithLocationIQ(form, { apiKey, language?, signal? })
 *
 * Esempio di utilizzo:
 * <SedeAziendale
 *   enableValidation={enableLegalAddressValidation}
 *   value={sedeAddressValues}
 *   onSedeFieldChange={onSedeFieldChange}
 *   hasStoredAddress={hasStoredAddress}
 *   addressNeedsReverify={addressNeedsReverify}
 *   onVerifiedMerge={onSedeVerifiedMerge}
 *   onResetToVerified={onSedeResetToVerified}
 *   onGeocodeServiceUnavailable={onGeocodeServiceUnavailable}
 *   apiKey={locationiqApiKeyProp || undefined}
 * />
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Close from "@mui/icons-material/Close";
import Search from "@mui/icons-material/Search";
import RestartAlt from "@mui/icons-material/RestartAlt";
import WarningAmber from "@mui/icons-material/WarningAmber";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { formatReadOnlyValue } from "../fields/ProfileReadOnlyField";
import { buildProfileInfoAdornment } from "../../profileInfoAdornment";
import {
  profileFieldsContentWidthSx,
  profileFormColumnSpacing,
  profileFormRowSpacing,
  profileReadOnlyTextFieldSx,
  profileSectionDividerSx,
  registrationTextFieldSx,
} from "../../profileFieldStyles";

// for theme alignment
const PRIMARY = "#0b3d87";

export const EMPTY_ADDRESS_FORM = {
  via: "",
  civico: "",
  citta: "",
  cap: "",
  provincia: "",
  regione: "",
  stato: "",
};

// constants

export const ADDRESS_FORM_FIELD_KEYS = ["via", "civico", "citta", "cap", "provincia", "regione", "stato"];

const OPTIONAL_CIVICO = new Set(["civico"]);

export const CIVICO_UNVERIFIED_TOOLTIP_IT =
  "Non abbiamo potuto verificare il numero civico. Lasciamo il valore che hai inserito, confermalo solo se sei sicuro che sia corretto.";

export const LOCATION_IQ_USER_MESSAGE_IT =
  "Servizio momentaneamente non disponibile. Ci scusiamo per il disagio, riprova più tardi.";

export const REGIONE_OPTIONAL_TOOLTIP_IT =
  "La regione è facoltativa: lasciala vuota per indirizzi fuori dall'Italia o quando non esiste un livello regionale. Se la includi sarà verificata.";

export const STATO_TOOLTIP_IT =
  "Inserisci il nome dello stato in italiano.";

export const ADDRESS_SERVICE_CODES = {
  MISSING_API_KEY: "MISSING_API_KEY",
  LOCATION_IQ_UNAVAILABLE: "LOCATION_IQ_UNAVAILABLE",
  ABORTED: "ABORTED",
  INCOMPLETE_FORM: "INCOMPLETE_FORM",
  VALIDATION_FAILED: "VALIDATION_FAILED",
};

const LOCATIONIQ_SEARCH_URL = "https://api.locationiq.com/v1/search";
const GLOBAL_STREET_PREFIX_RE = /^(?:via|viale|vico|piazza|strada|corso|largo|rue|avenue|ave|blvd|boulevard|calle|camino|carrera|carretera|rua|straat|str.|straße|ulica|road|rd\.?|st\.?|place|pl\.?|alley|drive|dr\.?|lane|ln\.?|court|ct\.?|square|sq\.?|terrace|ter\.?|way|wy\.?|esplanade|promenade|quai|chemin|impasse|passage|piazzale|vicolo|avenida|allee|trau)\b[.,\s;-]*/i;

// logic

export function stripStreetPrefix(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!GLOBAL_STREET_PREFIX_RE.test(trimmed)) {
    return trimmed;
  }

  const afterFirst = trimmed.replace(GLOBAL_STREET_PREFIX_RE, "").trim();
  if (!GLOBAL_STREET_PREFIX_RE.test(afterFirst)) {
    return afterFirst;
  }

  const firstPrefix = trimmed.match(GLOBAL_STREET_PREFIX_RE)?.[0].match(/^(\w+)/i)?.[1]?.toLowerCase();
  const secondPrefix = afterFirst.match(GLOBAL_STREET_PREFIX_RE)?.[0].match(/^(\w+)/i)?.[1]?.toLowerCase();

  if (firstPrefix && firstPrefix === secondPrefix) {
    return afterFirst.replace(GLOBAL_STREET_PREFIX_RE, "").trim();
  }

  return trimmed;
}

function normalizeAddressString(s) {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function bigrams(s) {
  const set = new Set();
  for (let i = 0; i < s.length - 1; i++) set.add(s[i] + s[i + 1]);
  return set;
}

function diceSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ba = bigrams(a);
  const bb = bigrams(b);
  let inter = 0;
  for (const g of ba) if (bb.has(g)) inter++;
  return (2 * inter) / (ba.size + bb.size);
}

// build the token pool
export function buildTokenPoolFromGeocodeHit(geocodeHit) {
  const tokens = new Set();
  const addr = geocodeHit?.address ?? {};
  for (const v of Object.values(addr)) {
    if (typeof v === "string" && v.trim()) tokens.add(v.trim());
  }
  const dn = geocodeHit?.display_name ?? "";
  for (const t of dn.split(",").map((x) => x.trim()).filter(Boolean)) tokens.add(t);
  return [...tokens];
}

// match the user's input to the token pool with a threshold score and returns the best match
export function matchUserValueToTokenPool(userValue, tokenPool, { viaMode = false } = {}) {
  const uNorm = normalizeAddressString(userValue);
  if (!uNorm) return null;
  const threshold = viaMode ? 0.55 : 0.45;
  let best = null;
  let bestScore = -1;
  for (const token of tokenPool) {
    const tNorm = normalizeAddressString(token);
    if (!tNorm) continue;
    if (uNorm === tNorm) return { token, score: 1 };
    if (tNorm.includes(uNorm) || uNorm.includes(tNorm)) {
      const score = 0.9;
      if (score > bestScore) {
        bestScore = score;
        best = token;
      }
      continue;
    }
    const score = diceSimilarity(uNorm, tNorm);
    if (score > bestScore) {
      bestScore = score;
      best = token;
    }
  }
  if (bestScore >= threshold) return { token: best, score: bestScore };
  return null;
}

export function isAddressFormComplete(form) {
  return ADDRESS_FORM_FIELD_KEYS.every((k) => {
    if (k === "regione") return true;
    return String(form[k] ?? "").trim().length > 0;
  });
}

function buildAllRejectedFields(form) {
  const fields = {};
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    const userVal = String(form[key] ?? "").trim();
    if (!userVal) {
      fields[key] = { filled: false, found: false, normalized: "", score: 0, source: "empty" };
    } else {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "geocode_error" };
    }
  }
  return fields;
}

// check if all non-cap and non-civico fields match
function allNonCapNonCivicoFieldsMatch(fields, form) {
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    if (key === "cap" || key === "civico") continue;
    if (key === "regione" && !String(form?.regione ?? "").trim()) continue;
    const fr = fields[key];
    if (!fr?.filled || !fr?.found) return false;
  }
  return true;
}

function applyPostcodeFromHitIfEligible(geocodeHit, fields, form) {
  const capFr = fields.cap;
  if (!capFr?.filled || capFr.found) return fields;
  if (!allNonCapNonCivicoFieldsMatch(fields, form)) return fields;
  const postcode = String(geocodeHit?.address?.postcode ?? "").trim();
  if (!postcode) return fields;
  return {
    ...fields,
    cap: { filled: true, found: true, normalized: postcode, score: 1, source: "address.postcode" },
  };
}

function computeOverallValidity(fields, form) {
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    if (key === "civico") continue;
    if (key === "regione" && !String(form?.regione ?? "").trim()) continue;
    const fr = fields[key];
    if (!fr?.filled || !fr?.found) return false;
  }
  if (String(form.via ?? "").trim() && !fields.via?.found) return false;
  return true;
}

export function validateGeocodeResultAgainstForm(geocodeHit, form) {
  const pool = buildTokenPoolFromGeocodeHit(geocodeHit);
  const fields = {};
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    const userVal = String(form[key] ?? "").trim();
    if (!userVal) {
      fields[key] = { filled: false, found: false, normalized: "", score: 0, source: "empty" };
      continue;
    }
    const match = matchUserValueToTokenPool(userVal, pool, { viaMode: key === "via" });
    if (match) {
      fields[key] = {
        filled: true,
        found: true,
        normalized: match.token,
        score: match.score,
        source: "token_pool",
      };
    } else if (OPTIONAL_CIVICO.has(key)) {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "not_found" };
    } else {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "not_found" };
    }
  }
  const fieldsWithPostcode = applyPostcodeFromHitIfEligible(geocodeHit, fields, form);
  const isValid = computeOverallValidity(fieldsWithPostcode, form);
  return { fields: fieldsWithPostcode, isValid, warnings: [] };
}

export function buildNormalizedFormFromFieldResults(form, fields) {
  const next = { ...form };
  for (const [key, fr] of Object.entries(fields)) {
    if (key === "civico") continue;
    if (fr.filled && fr.found && fr.normalized) next[key] = fr.normalized;
  }
  return next;
}

export function buildLocationIQSearchParams(form, { apiKey, language }) {
  const params = new URLSearchParams({
    key: apiKey,
    format: "json",
    addressdetails: "1",
    normalizeaddress: "1",
    normalizecity: "1",
    limit: "1",
    dedupe: "1",
    "accept-language": language,
  });
  const rawVia = String(form.via ?? "").trim();
  const viaForStreet = stripStreetPrefix(rawVia);
  const street = [viaForStreet, String(form.civico ?? "").trim()].filter(Boolean).join(" ");
  if (street) params.set("street", street);
  if (String(form.citta ?? "").trim()) params.set("city", String(form.citta).trim());
  if (String(form.cap ?? "").trim()) params.set("postalcode", String(form.cap).trim());
  if (String(form.provincia ?? "").trim()) params.set("county", String(form.provincia).trim());
  if (String(form.regione ?? "").trim()) params.set("state", String(form.regione).trim());
  if (String(form.stato ?? "").trim()) params.set("country", String(form.stato).trim());
  return params;
}

export function parseLocationIQSearchFirstHit(data) {
  if (Array.isArray(data) && data.length > 0) return data[0];
  if (data && typeof data === "object" && data.place_id != null) return data;
  return null;
}

function isLocationIQErrorPayload(data) {
  return data && typeof data === "object" && typeof data.error === "string" && data.error.trim().length > 0;
}

export async function validateAddressWithLocationIQ(form, options) {
  const { apiKey, language = "it", signal } = options ?? {};
  if (!String(apiKey ?? "").trim()) return { ok: false, code: ADDRESS_SERVICE_CODES.MISSING_API_KEY };
  if (!isAddressFormComplete(form)) return { ok: false, code: ADDRESS_SERVICE_CODES.INCOMPLETE_FORM };
  const params = buildLocationIQSearchParams(form, { apiKey, language });
  let response;
  try {
    response = await fetch(`${LOCATIONIQ_SEARCH_URL}?${params}`, { signal });
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, code: ADDRESS_SERVICE_CODES.ABORTED };
    return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  }
  let data;
  try {
    data = await response.json();
  } catch {
    return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  }
  if (isLocationIQErrorPayload(data)) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  if (response.status === 404) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  if (!response.ok) return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  const hit = parseLocationIQSearchFirstHit(data);
  if (!hit) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  const { fields, isValid } = validateGeocodeResultAgainstForm(hit, form);
  if (!isValid) return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields };
  const normalizedForm = buildNormalizedFormFromFieldResults(form, fields);
  return { ok: true, hit, fields, normalizedForm, warnings: [] };
}

// UI

// UI constants
const CIVICO_TOOLTIP_PROPS = { enterTouchDelay: 0, disableInteractive: true };

function fingerprintAddressForm(f) {
  const o = { ...EMPTY_ADDRESS_FORM, ...(f ?? {}) };
  return ADDRESS_FORM_FIELD_KEYS.map((k) => String(o[k] ?? "").trim()).join("\u001f");
}

const gridSx = {
  display: "grid",
  width: "100%",
  columnGap: profileFormColumnSpacing,
  rowGap: profileFormRowSpacing,
  px: { xs: 4, sm: 0 },
  gridTemplateColumns: { xs: "repeat(5, minmax(0, 1fr))", lg: "repeat(7, minmax(0, 1fr))" },
};

const colSpan = {
  via: { xs: "span 5", lg: "span 4" },
  civico: { xs: "span 2", lg: "span 1" },
  citta: { xs: "span 3", lg: "span 2" },
  cap: { xs: "span 2", lg: "span 1" },  
  provincia: { xs: "span 3", lg: "span 2" },
  regione: { xs: "span 3", lg: "span 2" },
  stato: { xs: "span 2", lg: "span 2" },
};

const buttonPrimarySx = {
  width: 180,
  fontFamily: "Rubik, sans-serif",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "10px",
  px: 2.5,
  py: 1,
  backgroundColor: PRIMARY,
  boxShadow: `0 6px 20px ${alpha(PRIMARY, 0.1)}`,
  "&:hover": { backgroundColor: "#092b6a" },
};

const buttonOutlinedSx = {
  width: 180,
  fontFamily: "Rubik, sans-serif",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "10px",
  px: 2.5,
  py: 1,
  color: PRIMARY,
  borderColor: alpha(PRIMARY, 0.45),
  "&:hover": { borderColor: PRIMARY, backgroundColor: alpha(PRIMARY, 0.06) },
};

// UI AddressField component

function AddressField({
  id,
  fieldKey,
  label,
  labelTooltip,
  gridColumn,
  value,
  onChange,
  placeholder,
  fieldResult,
  loading,
  readOnly = false,
}) {
  if (readOnly) {
    return (
      <Box sx={{ gridColumn, minWidth: 0 }}>
        <TextField
          id={id}
          label={label}
          value={formatReadOnlyValue(value)}
          fullWidth
          variant="outlined"
          size="medium"
          sx={{
            ...profileReadOnlyTextFieldSx,
            "& .MuiOutlinedInput-root": { ...profileReadOnlyTextFieldSx["& .MuiOutlinedInput-root"], minWidth: 0 },
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ readOnly: true, "aria-readonly": true }}
          InputProps={{
            readOnly: true,
            endAdornment: buildProfileInfoAdornment(labelTooltip),
          }}
        />
      </Box>
    );
  }

  const fr = fieldResult;
  const validated = !!fr;
  const optional = OPTIONAL_CIVICO.has(fieldKey);
  const error = validated && fr.filled && !fr.found && !optional;
  const outlineOverride =
    validated && fr.filled && fr.found
      ? { "& .MuiOutlinedInput-root fieldset": { borderColor: alpha("#2e7d32", 0.5) } }
      : validated && fr.filled && !fr.found && optional
        ? { "& .MuiOutlinedInput-root fieldset": { borderColor: alpha("#ed6c02", 0.55) } }
        : {};
  let adornment = null;
  if (loading) adornment = <CircularProgress size={18} thickness={5} sx={{ color: PRIMARY }} />;
  else if (validated && fr.filled) {
    if (!fr.found) {
      adornment = optional ? (
        <Tooltip title={CIVICO_UNVERIFIED_TOOLTIP_IT} arrow {...CIVICO_TOOLTIP_PROPS}>
          <span>
            <WarningAmber sx={{ fontSize: 20, color: "warning.main", cursor: "help", display: "block" }} />
          </span>
        </Tooltip>
      ) : (
        <Close sx={{ fontSize: 20, color: "error.main" }} />
      );
    } else adornment = <CheckCircleOutline sx={{ fontSize: 20, color: "success.main" }} />;
  }

  const infoAdornment = labelTooltip ? (
    <InputAdornment position="end">
      <Tooltip title={labelTooltip} arrow {...CIVICO_TOOLTIP_PROPS}>
        <IconButton
          size="small"
          edge="end"
          aria-label="Informazioni"
          tabIndex={-1}
          sx={{ p: 0.25, color: PRIMARY, "&:hover": { bgcolor: "transparent" } }}
        >
          <InfoOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  ) : null;

  const fieldSx = {
    ...registrationTextFieldSx,
    ...outlineOverride,
    "& .MuiOutlinedInput-root": { ...registrationTextFieldSx["& .MuiOutlinedInput-root"], minWidth: 0 },
    "& .MuiFormHelperText-root": { display: "none" },
  };

  return (
    <Box sx={{ gridColumn, minWidth: 0 }}>
      <TextField
        id={id}
        label={label}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        error={error}
        variant="outlined"
        size="medium"
        sx={fieldSx}
        InputProps={{
          endAdornment: adornment ? (
            <InputAdornment position="end">{adornment}</InputAdornment>
          ) : (
            infoAdornment
          ),
        }}
      />
    </Box>
  );
}

// UI SedeAziendale component

export function SedeAziendale({
  enableValidation = false,
  value,
  onSedeFieldChange,
  hasStoredAddress = false,
  addressNeedsReverify = false,
  onVerifiedMerge,
  onResetToVerified,
  onGeocodeServiceUnavailable,
  apiKey = process.env.REACT_APP_LOCATIONIQ_API_KEY ?? "",
  language = "it",
  readOnly = false,
}) {
  const form = value ?? EMPTY_ADDRESS_FORM;
  const [fieldRes, setFieldRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const pendingVerifiedMergeFingerprintRef = useRef(null);
  const retainedFieldResFingerprintRef = useRef(null);
  const addressFingerprint = fingerprintAddressForm(form);

  useEffect(() => {
    const fp = addressFingerprint;
    const pending = pendingVerifiedMergeFingerprintRef.current;
    if (pending != null && fp === pending) {
      retainedFieldResFingerprintRef.current = fp;
      pendingVerifiedMergeFingerprintRef.current = null;
      return;
    }
    if (retainedFieldResFingerprintRef.current === fp) return;
    retainedFieldResFingerprintRef.current = null;
    setFieldRes(null);
  }, [addressFingerprint]);

  const showVerifyReset = enableValidation && !readOnly && (!hasStoredAddress || addressNeedsReverify);

  const run = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setFieldRes(null);
    const result = await validateAddressWithLocationIQ(form, {
      apiKey,
      language,
      signal: abortRef.current.signal,
    });
    const serviceDown =
      result.ok === false &&
      (result.code === ADDRESS_SERVICE_CODES.MISSING_API_KEY ||
        result.code === ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE);
    if (serviceDown) onGeocodeServiceUnavailable?.();
    if (!result.ok) {
      setLoading(false);
      if (result.code === ADDRESS_SERVICE_CODES.ABORTED) return;
      if (result.code === ADDRESS_SERVICE_CODES.VALIDATION_FAILED && result.fields) setFieldRes(result.fields);
      return;
    }
    setFieldRes(result.fields);
    setLoading(false);
    pendingVerifiedMergeFingerprintRef.current = fingerprintAddressForm(result.normalizedForm);
    onVerifiedMerge?.({ ...result.normalizedForm });
  }, [apiKey, form, language, onGeocodeServiceUnavailable, onVerifiedMerge]);

  const setField = (sedeKey) => (val) => onSedeFieldChange?.(sedeKey, val);

  const handleReset = () => {
    setFieldRes(null);
    retainedFieldResFingerprintRef.current = null;
    pendingVerifiedMergeFingerprintRef.current = null;
    abortRef.current?.abort();
    onResetToVerified?.();
  };

  const handleKeyDown = (e) => {
    if (!showVerifyReset) return;
    if (e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  const canValidate = enableValidation && Boolean(String(apiKey ?? "").trim()) && !loading;

  return (
    <Box
      sx={{
        ...profileFieldsContentWidthSx,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
      onKeyDown={handleKeyDown}
    >
      <Box sx={gridSx}>
        <AddressField
          id="liqf-via"
          fieldKey="via"
          label="Via / Piazza"
          gridColumn={colSpan.via}
          placeholder="Leonardo da Vinci"
          value={form.via}
          onChange={setField("via")}
          fieldResult={fieldRes?.via}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-civico"
          fieldKey="civico"
          label="Civico"
          gridColumn={colSpan.civico}
          placeholder="14"
          value={form.civico}
          onChange={setField("civico")}
          fieldResult={fieldRes?.civico}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-citta"
          fieldKey="citta"
          label="Città"
          gridColumn={colSpan.citta}
          placeholder="Milano"
          value={form.citta}
          onChange={setField("citta")}
          fieldResult={fieldRes?.citta}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-cap"
          fieldKey="cap"
          label="CAP"
          gridColumn={colSpan.cap}
          placeholder="20121"
          value={form.cap}
          onChange={setField("cap")}
          fieldResult={fieldRes?.cap}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-prov"
          fieldKey="provincia"
          label="Provincia"
          gridColumn={colSpan.provincia}
          placeholder="Milano"
          value={form.provincia}
          onChange={setField("provincia")}
          fieldResult={fieldRes?.provincia}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-reg"
          fieldKey="regione"
          label="Regione"
          labelTooltip={REGIONE_OPTIONAL_TOOLTIP_IT}
          gridColumn={colSpan.regione}
          placeholder="Lombardia"
          value={form.regione}
          onChange={setField("regione")}
          fieldResult={fieldRes?.regione}
          loading={loading}
          readOnly={readOnly}
        />
        <AddressField
          id="liqf-stato"
          fieldKey="stato"
          label="Stato"
          labelTooltip={STATO_TOOLTIP_IT}
          gridColumn={colSpan.stato}
          placeholder="Italia"
          value={form.stato}
          onChange={setField("stato")}
          fieldResult={fieldRes?.stato}
          loading={loading}
          readOnly={readOnly}
        />
      </Box>

      {showVerifyReset ? (
        <>
          <Divider sx={{ ...profileSectionDividerSx, my: { xs: 3.5, sm: 4 } }} />
          <Stack direction="row" spacing={1.5} justifyContent="right" sx={{ width: "100%" }}>
            <Button
              type="button"
              variant="contained"
              onClick={() => run()}
              disabled={!canValidate}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              sx={{ display: "flex", alignItems: "center", ...buttonPrimarySx }}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ display: "flex", alignItems: "center" }}>{loading ? "Validazione…" : "Verifica"}</span>
                <Typography
                  sx={{
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "0.52rem",
                    color: "#ffffff",
                    lineHeight: 0.5,
                    display: "block",
                    textAlign: "left",
                    m: 0,
                  }}
                  component="span"
                >
                  <a
                    href="https://locationiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    by LocationIQ.com
                  </a>
                </Typography>
              </span>
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              startIcon={<RestartAlt />}
              sx={buttonOutlinedSx}
            >
              Reset
            </Button>
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

export default SedeAziendale;